import { appProvider } from '@/app-providers'
import { snackController } from '@/components/snack-bar/snack-bar-controller'
import { FarmTypes, fn100, LockPeriods, Zero } from '@/constants'
import { bnHelper } from '@/helpers/bignumber-helper'
import { FarmLPHandler } from '@/helpers/farmLP-handler'
import { PancakeRouterHandler } from '@/helpers/pancakeRouter-handler'
import { walletStore } from '@/stores/wallet-store'
import { FixedNumber } from '@ethersproject/bignumber'
import { get } from 'lodash-es'
import { action, computed, IReactionDisposer, observable, reaction, runInAction, when } from 'mobx'
import { asyncAction } from 'mobx-utils'
import moment from 'moment'
import { Subject, timer } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

export class FarmingViewModel {
  _disposers: IReactionDisposer[] = []
  private _unsubcrible = new Subject()

  pancakeRouterHandler?: PancakeRouterHandler
  farmLPHandler?: FarmLPHandler
  @observable farmType = 'sb'
  @observable ratio = Zero
  @observable rewardAmount = Zero
  @observable stakedLP = Zero
  @observable userLPBalance = Zero
  @observable userUSDBalance = Zero
  @observable userLength = 0
  @observable userBooster = 1
  @observable totalLockedAmount = Zero
  @observable estimateLP = Zero
  @observable lpInfo: any

  @observable farmLPBalance = Zero
  @observable totalLiquidity = Zero
  @observable annualPercentageRate = Zero
  @observable currentLockPeriod = LockPeriods['7']

  @observable lpInputAmount = ''
  @observable lockDuration = moment.duration(0, 'second')
  @observable lastStakeTime: moment.Moment | null = null
  @observable increaseLockUntil: moment.Moment = moment().add(this.currentLockPeriod.value, 'day')

  @observable tokenApproved = false
  @observable usdApproved = false
  @observable lpApproved = false
  @observable tokenApproving = false
  @observable usdApproving = false
  @observable lpApproving = false
  @observable pkRouterLPApproved = false
  @observable unstaking = false
  @observable isDialogLoading = false

  @observable tokenInputAmount = ''
  @observable usdInputAmount = ''

  @observable pairStaking = false
  @observable lpStaking = false
  @observable lpReceived = ''

  constructor(farmType, locktime) {
    if (!LockPeriods[`${locktime}`] || !FarmTypes.includes(farmType)) appProvider.router.replace('/farms')

    runInAction(() => {
      this.farmType = farmType
      this.currentLockPeriod = LockPeriods[locktime]
      this.increaseLockUntil = moment().add(this.currentLockPeriod.value, 'day')
    })

    this.loadData()
    this._disposers = [
      reaction(
        () => walletStore.account,
        () => {
          if (walletStore.isChainIdValid) {
            this.farmLPHandler?.injectMetamask(walletStore.web3!)
            this.pancakeRouterHandler?.injectMetamask(walletStore.web3!)
          }
        }
      )
    ]
  }

  destroy() {
    this._unsubcrible.next()
    this._unsubcrible.complete()
    this._disposers.forEach(d => d())
  }

  @asyncAction *loadData() {
    const pancake = new PancakeRouterHandler(this.farmType)
    this.pancakeRouterHandler = pancake
    const farmLPHandler = new FarmLPHandler(this.farmType, this.currentLockPeriod.value)
    this.farmLPHandler = farmLPHandler
    yield farmLPHandler.load()

    timer(0, 15000)
      .pipe(takeUntil(this._unsubcrible))
      .subscribe(async () => {
        this.getLPRatio()
        this.getLPInfo()
        this.getFarmLiquidityAndApy()
        this.getUserLength()
        const lockedAmount = await farmLPHandler.getTotalLockedAmount(0)
        runInAction(() => {
          this.totalLockedAmount = lockedAmount
        })
      })

    this._disposers.push(
      when(
        () => walletStore.connected,
        async () => {
          if (walletStore.chainId == process.env.VUE_APP_CHAIN_ID) {
            farmLPHandler.injectMetamask(walletStore.web3!)
            pancake.injectMetamask(walletStore.web3!)
            this.checkApproved()

            timer(0, 10000)
              .pipe(takeUntil(this._unsubcrible))
              .subscribe(async () => {
                this.fetchPoolInfo()
              })
          }
        }
      )
    )
  }

  @asyncAction *getLPInfo() {
    const lpInfo = yield this.farmLPHandler?.getLPInfo()
    this.lpInfo = lpInfo
  }

  @asyncAction *checkApproved() {
    const account = walletStore.account
    const [tokenApproved, usdApproved, lpApproved, pkRouterLPApproved] = yield Promise.all([
      this.farmLPHandler?.tokenApproved(account),
      this.farmLPHandler?.usdApproved(account),
      this.farmLPHandler?.lpApproved(account),
      this.pancakeRouterHandler?.approved(account)
    ])
    this.tokenApproved = tokenApproved
    this.usdApproved = usdApproved
    this.lpApproved = lpApproved
    this.pkRouterLPApproved = pkRouterLPApproved
  }

  @asyncAction *getLPRatio() {
    const ratio = yield this.farmLPHandler?.getLPRatio()
    this.ratio = ratio
  }

  @asyncAction *fetchPoolInfo() {
    if (!walletStore.account || !this.farmLPHandler) {
      //
    } else {
      this.getUserInfo()

      const [amount, userLPBalance, userUSDBalance, rewardAmount] = yield Promise.all([
        this.farmLPHandler.getUserStakeBalance(0, walletStore.account),
        this.farmLPHandler.getUserLPBalance(walletStore.account),
        this.farmLPHandler.getUserUSDBalance(walletStore.account),
        this.farmLPHandler.getRewardAmount(0, walletStore.account)
      ])
      this.stakedLP = amount
      this.userLPBalance = userLPBalance
      this.userUSDBalance = userUSDBalance
      this.rewardAmount = rewardAmount
    }
  }

  @asyncAction *addLpAndStake() {
    if (
      bnHelper.lt(
        this.farmType === 'SB' ? walletStore.sbBalance : walletStore.steBalance,
        FixedNumber.from(this.tokenInputAmount)
      )
    ) {
      snackController.error('$' + `${this.farmType} - Balance insufficient!`)
      return
    }
    if (bnHelper.lt(this.userUSDBalance, FixedNumber.from(this.usdInputAmount))) {
      snackController.commonError('$USDT - Balance insufficient!')
      return
    }

    this.pairStaking = true
    const tokenAmount = FixedNumber.from(this.tokenInputAmount)
    const usdAmount = FixedNumber.from(this.usdInputAmount)
    const fnSlippage = FixedNumber.from(this.slippage)

    const _minRewardToken = tokenAmount.subUnsafe(tokenAmount.mulUnsafe(fnSlippage.divUnsafe(fn100)))
    const _minUsdToken = usdAmount.subUnsafe(usdAmount.mulUnsafe(fnSlippage.divUnsafe(fn100)))

    try {
      yield this.farmLPHandler?.addLpAndStake(
        0,
        this.farmLPHandler.tokenAddress,
        process.env.VUE_APP_USD_ADDRESS,
        this.tokenInputAmount,
        this.usdInputAmount,
        _minRewardToken,
        _minUsdToken,
        moment.duration(this.currentLockPeriod.days, 'day').asSeconds(),
        walletStore.account
      )
      yield this.fetchPoolInfo()
      if (this.increasePairState) this.increasePairState = false
      snackController.success('Stake successfully')
      this.resetInput()
    } catch (error) {
      snackController.commonError(error)
    } finally {
      this.pairStaking = false
    }
  }

  @action resetInput() {
    this.tokenInputAmount = ''
    this.usdInputAmount = ''
    this.estimateLP = Zero
  }

  @asyncAction *stake() {
    this.lpStaking = true
    try {
      yield this.farmLPHandler?.stake(
        0,
        this.lpInputAmount,
        moment.duration(this.currentLockPeriod.days, 'day').asSeconds(),
        walletStore.account
      )
      this.fetchPoolInfo()
      snackController.success('Stake LP successfully')
      this.lpInputAmount = ''
    } catch (error) {
      snackController.commonError(error)
    } finally {
      this.lpStaking = false
    }
  }

  @asyncAction *getUserLength() {
    const userLength = yield this.farmLPHandler?.getUserLength(0)
    this.userLength = userLength
  }

  @asyncAction *getUserInfo() {
    const {
      amount,
      rewardDebt,
      rewardAmount,
      extraAmount,
      lastStakeTime,
      lockTime
    } = yield this.farmLPHandler?.getUserInfo(0, walletStore.account)

    this.lastStakeTime = lastStakeTime
    this.lockDuration = lockTime
    this.userBooster = get(LockPeriods[this.lockInDays], 'multiplier', 1)
  }

  @observable lpValue = Zero;
  @asyncAction *getFarmLiquidityAndApy() {
    const { poolLiquidityUsd, apy, farmLPBalance, lpValue } = yield this.farmLPHandler?.getFarmLiquidityAndApy()
    this.totalLiquidity = poolLiquidityUsd
    this.farmLPBalance = farmLPBalance
    this.annualPercentageRate = apy
    this.lpValue = lpValue
  }

  @asyncAction *increaseLPAmount() {
    this.lpStaking = true
    try {
      yield this.farmLPHandler?.stake(
        0,
        this.lpInputAmount,
        moment.duration(this.currentLockPeriod.days, 'day').asSeconds(),
        walletStore.account
      )
      this.fetchPoolInfo()
      snackController.success('Increase amount successfully!')
      this.increaseLPState = false
    } catch (error) {
      snackController.commonError(error)
    } finally {
      this.lpStaking = false
    }
  }

  @asyncAction *tokenApprove() {
    this.tokenApproving = true
    try {
      yield this.farmLPHandler?.tokenApprove(walletStore.account)
      this.tokenApproved = true
    } catch (error) {
      this.tokenApproved = false
      snackController.commonError(error)
    } finally {
      this.tokenApproving = false
    }
  }
  @asyncAction *usdApprove() {
    this.usdApproving = true
    try {
      yield this.farmLPHandler?.usdApprove(walletStore.account)
      this.usdApproved = true
    } catch (error) {
      this.usdApproved = false
      snackController.commonError(error)
    } finally {
      this.usdApproving = false
    }
  }
  @asyncAction *lpApprove() {
    this.lpApproving = true
    try {
      yield this.farmLPHandler?.lpApprove(walletStore.account)
      this.lpApproved = true
    } catch (error) {
      this.lpApproved = false
      snackController.commonError(error)
    } finally {
      this.lpApproving = false
    }
  }
  @observable pkRouterLPApproving = false;
  @asyncAction *pkRouterLPApprove() {
    this.pkRouterLPApproving = true
    try {
      yield this.pancakeRouterHandler?.approve(walletStore.account)
      this.pkRouterLPApproved = true
    } catch (error) {
      this.pkRouterLPApproved = false
      snackController.commonError(error)
    } finally {
      this.pkRouterLPApproving = false
    }
  }

  @asyncAction *confirmUnstake() {
    this.unstaking = true
    try {
      const amount = yield this.farmLPHandler?.unstake(0, walletStore.account)
      this.lpReceived = amount
      snackController.success('Unstake successfully')
      this.fetchPoolInfo()
      this.lpReceivedDialog = true
      this.unstakeState = false
    } catch (err) {
      snackController.commonError(err)
    } finally {
      this.unstaking = false
    }
  }

  @observable removing = false;
  @asyncAction *removeLiquidity() {
    this.removing = true
    try {
      yield this.pancakeRouterHandler?.removeLiquidity(
        this.farmType,
        this.lpInputAmount,
        this.estimateTokenReceived,
        this.estimateUsdReceived,
        walletStore.account
      )
      snackController.success('Remove liquidity successfully')
      this.removeLiquidityDialog = false
    } catch (error) {
      snackController.commonError(error)
    } finally {
      this.removing = false
    }
  }

  @asyncAction *withdraw() {
    this.isDialogLoading = true
    try {
      yield this.farmLPHandler?.harvest(0, walletStore.account)
      this.fetchPoolInfo()
      snackController.success('Havest successfully')
      this.withdrawState = false
    } catch (error) {
      snackController.commonError(error)
    } finally {
      this.isDialogLoading = false
    }
  }

  @action.bound calculateLP() {
    try {
      const lp1 = (this.lpInfo.totalSupplyLP as FixedNumber)
        .mulUnsafe(FixedNumber.from(this.tokenInputAmount))
        .divUnsafe(this.lpInfo.tokenBalance as FixedNumber)
      const lp2 = (this.lpInfo.totalSupplyLP as FixedNumber)
        .mulUnsafe(FixedNumber.from(this.usdInputAmount))
        .divUnsafe(this.lpInfo.usdBalance as FixedNumber)

      const fn100 = FixedNumber.from('100')
      const fnSlippage = FixedNumber.from(this.slippage)

      if (bnHelper.lt(lp1, lp2)) {
        this.estimateLP = lp1.subUnsafe(lp1.mulUnsafe(fnSlippage.divUnsafe(fn100)))
      } else {
        this.estimateLP = lp2.subUnsafe(lp2.mulUnsafe(fnSlippage.divUnsafe(fn100)))
      }
    } catch (error) {
      this.estimateLP = Zero
    }
  }
  @action.bound calculateUsdAmount(tokenAmount: string) {
    this.tokenInputAmount = tokenAmount
    try {
      const usdFN = this.ratio.mulUnsafe(FixedNumber.from(tokenAmount))
      this.usdInputAmount = usdFN.toString()
      this.calculateLP()
    } catch (error) {
      this.usdInputAmount = ''
      this.estimateLP = Zero
    }
  }
  @action.bound calculateTokenAmount(usdAmount: string) {
    this.usdInputAmount = usdAmount
    try {
      const tokenAmountFN = FixedNumber.from(usdAmount).divUnsafe(this.ratio)
      this.tokenInputAmount = tokenAmountFN.toString()
      this.calculateLP()
    } catch (error) {
      this.tokenInputAmount = ''
      this.estimateLP = Zero
    }
  }

  @action.bound maxLPBalance() {
    this.lpInputAmount = this.userLPBalance.toString()
  }

  @action.bound changeLPInputAmount(amount) {
    this.lpInputAmount = amount
  }

  @observable slippage = '1'
  @action.bound onSlippageChanged(val) {
    this.slippage = val
  }

  @observable increaseLPState = false
  @observable increasePairState = false
  @observable increaseStakePairTimeUntil = moment()
  @observable increaseStakeLPTimeUntil = moment()

  @action.bound increaseAmountDialog(val: boolean) {
    if (this.hasLPToken) {
      this.lpInputAmount = ''
      this.increaseLPState = val
      if (val) {
        this.increaseStakeLPTimeUntil = moment().add(
          moment.duration(this.currentLockPeriod.days, 'day').asSeconds(),
          'seconds'
        )
      }
    } else {
      this.resetInput()
      this.increasePairState = val
      if (val) {
        this.increaseStakePairTimeUntil = moment().add(
          moment.duration(this.currentLockPeriod.days, 'day').asSeconds(),
          'seconds'
        )
      }
    }
  }
  @observable increaseTimeState = false
  @action.bound increaseTimeDialog(val: boolean) {
    this.increaseTimeState = val
  }
  @observable unstakeState = false
  @action.bound unstakeDialog(val: boolean) {
    this.unstakeState = val
  }
  @observable lpReceivedDialog = false
  @action.bound changeLPReceivedDialog(val: boolean) {
    this.lpReceivedDialog = val
  }
  @observable withdrawState = false
  @action.bound withdrawDialog(val: boolean) {
    this.withdrawState = val
  }
  @observable removeLiquidityDialog = false
  @action.bound changeRemoveLiqDialog(val) {
    this.lpInputAmount = ''
    this.removeLiquidityDialog = val
  }

  @computed get hasLPToken() {
    return bnHelper.gt(this.userLPBalance, Zero)
  }
  @computed get isStaking() {
    return bnHelper.gt(this.stakedLP, Zero)
  }
  @computed get isStaked() {
    return !!this.lastStakeTime
  }
  @computed get unstakeTime() {
    return this.lastStakeTime?.clone().add(this.lockDuration)
  }
  @computed get canUnstake() {
    return moment().isAfter(this.unstakeTime) && bnHelper.gt(this.stakedLP, Zero)
  }
  @computed get canHarvest() {
    return bnHelper.gt(this.rewardAmount, Zero)
  }
  @computed get lpStakeInput() {
    try {
      return FixedNumber.from(this.lpInputAmount)
    } catch (error) {
      return Zero
    }
  }
  @computed get estimateTokenReceived() {
    try {
      return this.lpStakeInput
        .mulUnsafe(this.lpInfo.tokenBalance)
        .divUnsafe(this.lpInfo.totalSupplyLP)
        .mulUnsafe(fn100.subUnsafe(FixedNumber.from(this.slippage)))
        .divUnsafe(fn100)
    } catch (error) {
      return Zero
    }
  }
  @computed get estimateUsdReceived() {
    try {
      return this.lpStakeInput
        .mulUnsafe(this.lpInfo.usdBalance)
        .divUnsafe(this.lpInfo.totalSupplyLP)
        .mulUnsafe(fn100.subUnsafe(FixedNumber.from(this.slippage)))
        .divUnsafe(fn100)
    } catch (error) {
      return Zero
    }
  }
  @computed get validLPInputAmount() {
    try {
      const amount = FixedNumber.from(this.lpInputAmount)
      return bnHelper.lte(amount, this.userLPBalance)
    } catch (error) {
      return false
    }
  }
  @computed get lockInDays() {
    return this.lockDuration.asDays()
  }
  @computed get defaultLocktimeInDays() {
    return moment.duration(this.currentLockPeriod.days, 'day').asDays()
  }
  @computed get lockInSeconds() {
    return this.lockDuration.asSeconds()
  }
  @computed get validLockPeriods() {
    return Object.values(LockPeriods).filter(period => {
      if (this.isStaked && period.value > this.lockInDays) return true
      else if (!this.isStaked && period.value >= this.lockInDays) return true
      return false
    })
  }
  @computed get validPairInput() {
    try {
      FixedNumber.from(this.tokenInputAmount)
      FixedNumber.from(this.usdInputAmount)

      return true
    } catch (error) {
      return false
    }
  }
  @computed get estimateUserAPR() {
    try {
      const booster = this.currentLockPeriod.multiplier
      return this.annualPercentageRate.mulUnsafe(FixedNumber.from(`${booster}`))
    } catch (error) {
      return this.annualPercentageRate
    }
  }
  @computed get estimateRewardAtIncreaseTime() {
    try {
      return this.estimateUserAPR.mulUnsafe(this.stakedLP).divUnsafe(FixedNumber.from('100'))
    } catch (error) {
      return Zero
    }
  }
  @computed get userAPR() {
    try {
      return this.annualPercentageRate.mulUnsafe(FixedNumber.from(`${this.userBooster}`))
    } catch (error) {
      return Zero
    }
  }
  @computed get estimateRewardInPair() {
    return this.stakedLP
      .addUnsafe(this.estimateLP)
      .mulUnsafe(this.estimateUserAPR)
      .divUnsafe(FixedNumber.from('100'))
  }
  @computed get estimateRewardInLP() {
    try {
      return this.stakedLP
        .addUnsafe(FixedNumber.from(this.lpInputAmount))
        .mulUnsafe(this.estimateUserAPR)
        .divUnsafe(FixedNumber.from('100'))
    } catch (error) {
      return this.stakedLP.mulUnsafe(this.estimateUserAPR).divUnsafe(FixedNumber.from('100'))
    }
  }
  @computed get totalStakeIncreaseInPair() {
    try {
      return this.stakedLP.addUnsafe(this.estimateLP)
    } catch (error) {
      return this.stakedLP
    }
  }
  @computed get totalStakeIncreaseInLP() {
    try {
      return this.stakedLP.addUnsafe(FixedNumber.from(this.lpInputAmount))
    } catch (error) {
      return this.stakedLP
    }
  }

  @computed get canRemoveLiquidity() {
    return bnHelper.gt(this.userLPBalance, Zero)
  }
}
